/**
 * 보청기 브랜드 공통 Mixin
 * - 브랜드 목록 조회 및 데이터 변환 로직 공통화
 * - brands 페이지, registration 페이지에서 사용
 */

import { fetchHearingAid } from '@/api';

/**
 * attachments에서 logo 타입 이미지 추출
 * @param {Array} attachments - 첨부파일 배열
 * @returns {string} - 로고 이미지 URL
 */
function extractLogoImage(attachments) {
  if (!attachments || attachments.length === 0) return '';

  const logoAttachment = attachments.find(att => att.type === 'logo');
  return logoAttachment ? logoAttachment.download_url : '';
}

/**
 * attachments에서 details 타입 이미지들 추출
 * @param {Array} attachments - 첨부파일 배열
 * @returns {Array} - 상세 이미지 URL 배열
 */
function extractDetailImages(attachments) {
  if (!attachments || attachments.length === 0) return [];

  return attachments
    .filter(att => att.type === 'details')
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(att => att.download_url);
}

export default {
  data() {
    return {
      brands: [],
      brandsLoading: false,
      brandsError: null
    };
  },
  methods: {
    /**
     * 보청기 브랜드 목록 조회
     * - API에서 데이터를 가져와 brands 배열에 저장
     */
    async loadBrands() {
      if (this.brandsLoading) return;

      this.brandsLoading = true;
      this.brandsError = null;

      try {
        const response = await fetchHearingAid();
        const apiBrands = response.data;

        // API 데이터를 UI 형식으로 변환
        this.brands = apiBrands.map(brand => ({
          id: brand.id,
          name: brand.name,
          description: this.stripHtmlTags(brand.description),
          logo: brand.logo_image_url || extractLogoImage(brand.attachments),
          detailImages: extractDetailImages(brand.attachments),
          outlink: brand.outlink_url,
          originalData: brand // 원본 데이터 보관
        }));

      } catch (error) {
        this.brandsError = '브랜드 정보를 불러오는 중 오류가 발생했습니다.';
        console.error('===== 브랜드 API 호출 에러 =====');
        console.error('에러:', error);
        console.error('에러 상세:', error.response?.data || error.message);
        console.error('============================');
      } finally {
        this.brandsLoading = false;
      }
    },

    /**
     * HTML 태그 제거 (description이 HTML 형식일 경우)
     * @param {string} html - HTML 문자열
     * @returns {string} - 순수 텍스트
     */
    stripHtmlTags(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },

    /**
     * 브랜드 선택 옵션 형식으로 변환
     * - select/dropdown에서 사용할 수 있는 형식
     * @returns {Array} - {value, label} 형식의 배열
     */
    getBrandOptions() {
      return this.brands.map(brand => ({
        value: brand.name,
        label: brand.name
      }));
    }
  }
};
